import React, { useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Divider,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Popover,
  Stack,
  Typography,
} from '@mui/material';
import { Error, EventBusy, School, Warning } from '@mui/icons-material';
import RecordMissingAgentAbsenceDialog from './RecordMissingAgentAbsenceDialog';
import {
  formatShiftTimeRange,
  getMissingAgentAbsenceNote,
  getMissingAgentChipLabel,
  getMissingAgentChipSx,
  getMissingAgentSecondaryText,
  getMissingAgentShiftPreview,
  getMissingAgentShifts,
  MissingAgentRow,
  TRAINING_CHIP_STYLE,
} from '../utils/attendanceLabels';
import { getApprovedAbsenceDisplayLabel } from '../constants/absenceReasons';

interface MissingAgentListItemProps {
  agent: MissingAgentRow;
  canRecordAbsence?: boolean;
  onAbsenceRecorded?: () => void;
}

const MissingAgentListItem: React.FC<MissingAgentListItemProps> = ({
  agent,
  canRecordAbsence = false,
  onAbsenceRecorded,
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [absenceDialogOpen, setAbsenceDialogOpen] = useState(false);
  const open = Boolean(anchorEl);
  const chipLabel = getMissingAgentChipLabel(agent);
  const chipSx = getMissingAgentChipSx(agent);
  const isApprovedAbsence = agent.status === 'absent_approved';
  const isOnTraining = agent.status === 'on_training';
  const shiftPreview = getMissingAgentShiftPreview(agent);
  const shifts = getMissingAgentShifts(agent);
  const absenceNote = getMissingAgentAbsenceNote(agent);
  const absenceTypeLabel = isApprovedAbsence && (agent.absenceType || agent.absenceReason)
    ? getApprovedAbsenceDisplayLabel({
        absenceType: agent.absenceType,
        reason: agent.absenceReason,
      })
    : null;

  const statusIcon = agent.status === 'unassigned' || agent.status === 'not_scheduled' ? (
    <EventBusy color="warning" />
  ) : isOnTraining ? (
    <School sx={{ color: TRAINING_CHIP_STYLE.color }} />
  ) : agent.status === 'no_show' || agent.status === 'no_show_auto' || agent.status === 'severely_late' ? (
    <Error color="error" />
  ) : (
    <Warning color={agent.status === 'absent_approved' ? 'info' : agent.status === 'late' ? 'warning' : 'info'} />
  );

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const showRecordAbsence = canRecordAbsence && !isApprovedAbsence && !isOnTraining;

  const handleOpenAbsenceDialog = () => {
    setAbsenceDialogOpen(true);
    handleClose();
  };

  const handleAbsenceRecorded = () => {
    setAbsenceDialogOpen(false);
    onAbsenceRecorded?.();
  };

  return (
    <>
      <ListItem disablePadding>
        <ListItemButton
          onClick={handleOpen}
          aria-label={`View shift details for ${agent.name}`}
          sx={{
            py: 0.75,
            borderRadius: 1,
            alignItems: 'flex-start',
            ...(isApprovedAbsence || isOnTraining
              ? {
                  bgcolor: isOnTraining ? 'rgba(178, 223, 219, 0.35)' : 'grey.100',
                  mb: 0.5,
                  '&:hover': {
                    bgcolor: isOnTraining ? 'rgba(178, 223, 219, 0.55)' : 'grey.200',
                  },
                }
              : {}),
          }}
        >
          <ListItemIcon sx={{ minWidth: 36, mt: 0.25 }}>
            {statusIcon}
          </ListItemIcon>
          <ListItemText
            primary={agent.name}
            primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
            secondary={(
              <Box component="span" sx={{ display: 'block', mt: 0.25 }}>
                <Typography variant="caption" display="block" color="text.secondary">
                  {agent.campaign} • {agent.department}
                </Typography>
                {shiftPreview && (
                  <Typography
                    variant="caption"
                    display="block"
                    color="text.secondary"
                    sx={{ fontSize: '0.7rem', letterSpacing: '0.02em' }}
                  >
                    {shiftPreview}
                  </Typography>
                )}
                <Typography
                  variant="caption"
                  display="block"
                  color="text.secondary"
                  sx={absenceNote ? {
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                  } : undefined}
                >
                  {getMissingAgentSecondaryText(agent)}
                </Typography>
              </Box>
            )}
          />
          <Chip label={chipLabel} size="small" sx={{ ...chipSx, ml: 1, flexShrink: 0 }} />
        </ListItemButton>
      </ListItem>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{
          sx: { width: 320, maxWidth: '90vw', p: 2 },
        }}
      >
        <Typography variant="subtitle1" fontWeight={700} gutterBottom>
          {agent.name}
        </Typography>
        <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
          {agent.employeeId ? `ID ${agent.employeeId} · ` : ''}
          {agent.department}
        </Typography>

        <Stack direction="row" spacing={1} sx={{ mb: 1.5 }}>
          <Chip label={chipLabel} size="small" sx={chipSx} />
          {(agent.minutesLate || 0) > 0 && (
            <Chip label={`${agent.minutesLate} min past start`} size="small" variant="outlined" />
          )}
        </Stack>

        <Typography variant="body2" color="text.secondary" sx={{ mb: absenceNote ? 0.75 : 1.5 }}>
          {absenceTypeLabel && absenceNote ? absenceTypeLabel : getMissingAgentSecondaryText(agent)}
        </Typography>

        {absenceNote && (
          <Box
            sx={{
              mb: 1.5,
              px: 1.25,
              py: 1,
              borderRadius: 1,
              bgcolor: 'action.hover',
            }}
          >
            <Typography variant="overline" color="text.secondary" display="block" sx={{ lineHeight: 1.2 }}>
              Reason
            </Typography>
            <Typography variant="body2" color="text.primary" sx={{ mt: 0.25, whiteSpace: 'pre-wrap' }}>
              {absenceNote}
            </Typography>
          </Box>
        )}

        {!absenceNote && <Box sx={{ mb: 0.75 }} />}

        <Divider sx={{ mb: 1.5 }} />

        <Typography variant="overline" color="text.secondary" display="block">
          Today&apos;s shifts
        </Typography>

        {shifts.length > 0 ? (
          <Stack spacing={1} sx={{ mt: 0.5 }}>
            {shifts.map((shift) => {
              const timeRange = formatShiftTimeRange(shift.scheduledStartTime, shift.scheduledEndTime);
              return (
                <Box
                  key={shift.scheduleId}
                  sx={{
                    px: 1.25,
                    py: 0.75,
                    borderRadius: 1,
                    bgcolor: 'action.hover',
                  }}
                >
                  <Typography variant="body2" fontWeight={600}>
                    {timeRange || 'Scheduled'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {shift.campaign}
                  </Typography>
                </Box>
              );
            })}
          </Stack>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            No scheduled shifts for today.
          </Typography>
        )}

        {agent.email && (
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1.5 }}>
            {agent.email}
          </Typography>
        )}

        {showRecordAbsence && (
          <Button
            variant="contained"
            size="small"
            fullWidth
            sx={{ mt: 2 }}
            onClick={handleOpenAbsenceDialog}
          >
            Record absence
          </Button>
        )}
      </Popover>

      {showRecordAbsence && (
        <RecordMissingAgentAbsenceDialog
          agent={agent}
          open={absenceDialogOpen}
          onClose={() => setAbsenceDialogOpen(false)}
          onSuccess={handleAbsenceRecorded}
        />
      )}
    </>
  );
};

export default MissingAgentListItem;
